import { useMutation, useSuspenseQuery } from '@apollo/client'
import { defineMessages, FormattedMessage, useIntl } from '@faceup/localization'
import { withModalSuspense } from '@faceup/ui'
import { type ModalProps, useMessage } from '@faceup/ui-base'
import { FrontendPermissionType } from '@faceup/utils'
import { type JSX, useState } from 'react'
import { graphql } from '../../__generated__'
import { useLastInvestigatorGuard } from '../../hooks'
import { useMotherId } from '../../providers'
import { LastInvestigatorModal } from '../LastInvestigatorModal'
import {
  AbstractMemberModal,
  type AccessKey,
  accesses,
  type FormValues,
} from './AbstractMemberModal'

const messages = defineMessages('institution.components.memberModal.editMemberModal', [
  { id: 'title', defaultMessage: 'Edit member' },
  {
    id: 'cantEditAccountOwner',
    defaultMessage: 'Account owner can’t be removed from access to settings.',
  },
  {
    id: 'permissionsChanged',
    defaultMessage: '{email}’s permissions were changed',
    values: { email: true },
  },
] as const)

const query = {
  EditMemberModalData: graphql(`
    query EditMemberModalDataQuery($memberId: UUID!, $motherId: UUID!) {
      memberViewer {
        id
        isAccountOwner(motherId: $motherId)
        ...AbstractMemberModal_member
      }
      institution(organizationalUnitId: $motherId) {
        id
        ...AbstractMemberModal_institution
      }
      member(memberId: $memberId, motherId: $motherId) {
        id
        isAccountOwner(motherId: $motherId)
        name
        phone
        companyIds(motherId: $motherId)
        email
        keys {
          id
          permissions(motherId: $motherId) {
            id
            type
            enabled
            additionalData {
              id
              categoryIds
            }
          }
        }
      }
    }
  `),
}

const mutations = {
  EditMember: graphql(`
    mutation EditMemberMutation($input: EditMemberInput!, $motherId: UUID!) {
      editMember(input: $input) {
        member {
          id
          name
          companyIds(motherId: $motherId)
        }
      }
    }
  `),
}

// The last-investigator guard only applies when `InvestigationAccess` is turned off (enabled→disabled).
const isRevokingInvestigationAccess = (
  currentPermissions: { type: FrontendPermissionType; enabled: boolean }[],
  nextPermissions: { type: FrontendPermissionType; enabled: boolean }[]
) => {
  const isEnabled = (permissions: { type: FrontendPermissionType; enabled: boolean }[]) =>
    permissions.find(permission => permission.type === FrontendPermissionType.InvestigationAccess)
      ?.enabled ?? false
  return isEnabled(currentPermissions) && !isEnabled(nextPermissions)
}

type EditMemberModalProps = {
  memberId: string
  onCompleted: () => void
  onClose: () => void
  // Forwarded to the LastInvestigatorModal's dead-end alert (see its prop docs).
  onOpenMembersSettings?: () => void
} & Required<Pick<ModalProps, 'open'>>

const _EditMemberModal = ({
  open,
  onClose,
  memberId,
  onCompleted,
  onOpenMembersSettings,
}: EditMemberModalProps) => {
  const { formatMessage } = useIntl()
  const message = useMessage()
  const { getMotherId } = useMotherId()
  const { data } = useSuspenseQuery(query.EditMemberModalData, {
    variables: { memberId, motherId: getMotherId() },
  })
  // The form values that were blocked by the guard — replayed via `onContinue` once the orphaned
  // investigations have been reassigned.
  const [pendingRevokeValues, setPendingRevokeValues] = useState<FormValues | null>(null)
  const { wouldOrphanInvestigations } = useLastInvestigatorGuard()

  const [editMember] = useMutation(mutations.EditMember, { onCompleted })

  const memberViewer = data.memberViewer
  const institution = data.institution
  const memberToEdit = data.member

  if (!memberToEdit || !institution) {
    onClose()
    return null
  }

  const isKredenc = !memberViewer

  const defaultValues = {
    email: memberToEdit.email ?? '',
    institutionIds: memberToEdit.companyIds,
    permissions: memberToEdit.keys.permissions,
  }

  const runEditMember = async (values: FormValues) => {
    const result = await editMember({
      variables: {
        motherId: getMotherId(),
        input: {
          organizationalUnitIds: values.institutionIds,
          permissions: values.permissions,
          motherId: getMotherId(),
          memberId: memberToEdit.id,
          name: memberToEdit.name ?? '',
          ...(isKredenc && { email: values.email }),
        },
      },
    })

    if (result.errors) {
      return false
    }

    message.success(
      formatMessage(messages.permissionsChanged, {
        values: { email: memberToEdit.email ?? '' },
      })
    )
    onClose()
    return true
  }

  return (
    <>
      <AbstractMemberModal
        modalVariant='edit'
        title={<FormattedMessage message={messages.title} />}
        // avoid double toast
        // runEditMember displays the success message instead of the default form one
        isSuccessMessageDisplayed={false}
        member={memberViewer}
        institution={institution}
        open={open}
        onClose={onClose}
        overrideDisabledMessageForType={(
          permissionType: FrontendPermissionType,
          originalMessage: JSX.Element | null
        ) => {
          if (
            accesses.settings.subItems?.map(({ type }) => type).includes(permissionType) &&
            memberToEdit.isAccountOwner
          ) {
            return <FormattedMessage message={messages.cantEditAccountOwner} />
          }

          return originalMessage
        }}
        overrideDisabledMessageForSection={(
          section: AccessKey,
          originalMessage: JSX.Element | null
        ) => {
          if (section === 'settings' && memberToEdit.isAccountOwner) {
            return <FormattedMessage message={messages.cantEditAccountOwner} />
          }

          return originalMessage
        }}
        defaultValues={defaultValues}
        onSubmit={async values => {
          // Block the revoke before it happens if it would orphan an investigation; the modal opens
          // and `editMember` never fires. The server guard remains the backstop for the check-then-act
          // race. Runs in both apps — kredenc now mounts tRPC too.
          if (
            isRevokingInvestigationAccess(memberToEdit.keys.permissions, values.permissions) &&
            (await wouldOrphanInvestigations(memberToEdit.id, getMotherId()))
          ) {
            // Stash the attempted values; `onContinue` replays the edit after reassignment.
            setPendingRevokeValues(values)
            return false
          }

          return runEditMember(values)
        }}
      />
      {pendingRevokeValues && (
        <LastInvestigatorModal
          memberId={memberToEdit.id}
          memberEmail={memberToEdit.email ?? ''}
          action='revoke'
          open
          onClose={() => setPendingRevokeValues(null)}
          onOpenMembersSettings={onOpenMembersSettings}
          onContinue={async () => {
            await runEditMember(pendingRevokeValues)
            setPendingRevokeValues(null)
          }}
        />
      )}
    </>
  )
}

export const EditMemberModal = withModalSuspense(_EditMemberModal)
